import React from 'react';
import { Bug, Zap, Settings, CheckCircle, Warehouse, Activity, ListOrdered, BarChart3, Barcode, Printer, Scan, Stethoscope } from 'lucide-react';
import { Card } from '../../../common/Card';
import { LoggingTest } from '../../../common/LoggingTest';
import { LocationActivityTest } from '../../../common/LocationActivityTest';
import { RuleGroupEngineTest } from './RuleGroupEngineTest';
import { WarehouseAPITest } from './WarehouseAPITest';
import { Fase3Test } from '../rack-builder-live/Fase3Test';
import { Fase5Test } from '../rack-builder-live/Fase5Test';
import Fase6Test from '../rack-builder-live/Fase6Test';
import Fase7Test from '../rack-builder-live/Fase7Test';
import Fase8Test from '../rack-builder-live/Fase8Test';
import { GS1TestComponent } from '../../../common/testing/GS1TestComponent';
import { GlobalScannerTest } from '../../../common/testing/GlobalScannerTest';
import { PrintTestComponent } from '../../../common/testing/PrintTestComponent';
import { PutAwayDiagnostics } from '../administration/PutAwayDiagnostics';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useSubModuleView } from '../../../../hooks/useSubModuleView';

type TestTab = 'rulegroup' | 'logging' | 'warehouse' | 'signalr' | 'queue' | 'cache' | 'heatmap' | 'statistics' | 'settings' | 'gs1' | 'scanner' | 'print' | 'putaway-diag' | 'performance' | 'validation';

interface TestTabInfo {
  id: TestTab;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
  available: boolean;
}

export const TestModule: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useSubModuleView<TestTab>('rulegroup', { landing: 'rulegroup' });

  const tabs: TestTabInfo[] = [
    {
      id: 'rulegroup',
      name: 'RuleGroupEngine',
      icon: Zap,
      description: t('manager.modules.testing.tabs.rulegroup.description', {}, 'Test systemregler og regelgrupper'),
      available: true
    },
    {
      id: 'logging',
      name: t('manager.modules.testing.tabs.logging.name', {}, 'Logging System'),
      icon: Bug,
      description: t('manager.modules.testing.tabs.logging.description', {}, 'Test logging funksjonalitet'),
      available: true
    },
    {
      id: 'warehouse',
      name: 'Warehouse API',
      icon: Warehouse,
      description: t('manager.modules.testing.tabs.warehouse.description', {}, 'Test warehouse endepunkter og data'),
      available: true
    },
    {
      id: 'signalr',
      name: 'SignalR Activity',
      icon: Activity,
      description: t('manager.modules.testing.tabs.signalr.description', {}, 'Test sanntids lokasjonsaktivitet'),
      available: true
    },
    {
      id: 'queue',
      name: 'Queue & Carousel',
      icon: ListOrdered,
      description: t('manager.modules.testing.tabs.queue.description', {}, 'Test event queue og automatisk reol-bytte'),
      available: true
    },
    {
      id: 'cache',
      name: 'Activity Cache',
      icon: Settings,
      description: t('manager.modules.testing.tabs.cache.description', {}, 'Test event cache, persistence og midnight reset'),
      available: true
    },
    {
      id: 'heatmap',
      name: 'Heatmap Viz',
      icon: BarChart3,
      description: t('manager.modules.testing.tabs.heatmap.description', {}, 'Test heatmap visualisering og intensitet'),
      available: true
    },
    {
      id: 'statistics',
      name: 'Statistics Panel',
      icon: Activity,
      description: t('manager.modules.testing.tabs.statistics.description', {}, 'Test statistikk, activity feed og top locations'),
      available: true
    },
    {
      id: 'settings',
      name: t('manager.modules.testing.tabs.settings.name', {}, 'Settings'),
      icon: Settings,
      description: t('manager.modules.testing.tabs.settings.description', {}, 'Test dashboard innstillinger og konfigurasjon'),
      available: true
    },
    {
      id: 'gs1',
      name: 'GS1-128 Parser',
      icon: Barcode,
      description: t('manager.modules.testing.tabs.gs1.description', {}, 'Test GS1-128 strekkoder med ~ separator'),
      available: true
    },
    {
      id: 'scanner',
      name: 'Global Scanner',
      icon: Scan,
      description: t('manager.modules.testing.tabs.scanner.description', {}, 'Test global strekkode-skanning uten tekstfelt'),
      available: true
    },
    {
      id: 'print',
      name: t('manager.modules.testing.tabs.print.name', {}, 'Utskrift'),
      icon: Printer,
      description: t('manager.modules.testing.tabs.print.description', {}, 'Test etikettutskrift og printerfunksjonalitet'),
      available: true
    },
    {
      id: 'putaway-diag',
      name: t('manager.modules.testing.tabs.putawayDiag.name', {}, 'Put-Away Diagnostikk'),
      icon: Stethoscope,
      description: t('manager.modules.testing.tabs.putawayDiag.description', {}, 'Diagnostiser og feilsøk vareplassering'),
      available: true
    },
    {
      id: 'performance',
      name: t('manager.modules.testing.tabs.performance.name', {}, 'Ytelse & Performance'),
      icon: Settings,
      description: t('manager.modules.testing.tabs.performance.description', {}, 'Test ytelse og performance overvåkning'),
      available: false // Placeholder for future implementation
    },
    {
      id: 'validation',
      name: t('manager.modules.testing.tabs.validation.name', {}, 'Data Validering'),
      icon: CheckCircle,
      description: t('manager.modules.testing.tabs.validation.description', {}, 'Test datavalidering og konsistens'),
      available: false // Placeholder for future implementation
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'rulegroup':
        return <RuleGroupEngineTest />;
      case 'logging':
        return <LoggingTest />;
      case 'warehouse':
        return <WarehouseAPITest />;
      case 'signalr':
        return <LocationActivityTest />;
      case 'queue':
        return <Fase3Test />;
      case 'cache':
        return <Fase5Test />;
      case 'heatmap':
        return <Fase6Test />;
      case 'statistics':
        return <Fase7Test />;
      case 'settings':
        return <Fase8Test />;
      case 'gs1':
        return <GS1TestComponent />;
      case 'scanner':
        return <GlobalScannerTest />;
      case 'print':
        return <PrintTestComponent />;
      case 'putaway-diag':
        return <PutAwayDiagnostics onBack={() => setActiveTab('rulegroup')} />;
      case 'performance':
        return (
          <Card className="p-6 text-center">
            <Settings className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">{t('manager.modules.testing.tabs.performance.name', {}, 'Ytelse & Performance')}</h3>
            <p className="text-gray-500">{t('manager.modules.testing.notImplemented', {}, 'Denne funksjonaliteten er ikke implementert ennå.')}</p>
          </Card>
        );
      case 'validation':
        return (
          <Card className="p-6 text-center">
            <CheckCircle className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">{t('manager.modules.testing.tabs.validation.name', {}, 'Data Validering')}</h3>
            <p className="text-gray-500">{t('manager.modules.testing.notImplemented', {}, 'Denne funksjonaliteten er ikke implementert ennå.')}</p>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center gap-3">
          <Bug size={32} />
          <div>
            <h1 className="text-2xl font-bold">{t('manager.modules.testing.header.title', {}, 'Test og Debugging')}</h1>
            <p className="text-blue-100">{t('manager.modules.testing.header.subtitle', {}, 'Test systemfunksjonalitet og feilsøking')}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <Card className="p-1">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isAvailable = tab.available;

            return (
              <button
                key={tab.id}
                onClick={() => isAvailable && setActiveTab(tab.id)}
                disabled={!isAvailable}
                className={`
                  p-4 rounded-lg text-center transition-all duration-200 relative
                  ${isActive && isAvailable
                    ? 'bg-blue-500 text-white shadow-md'
                    : isAvailable
                      ? 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed opacity-60'
                  }
                `}
                title={isAvailable ? tab.description : t('manager.modules.testing.notAvailable', {}, 'Ikke tilgjengelig')}
              >
                <Icon size={24} className="mx-auto mb-2" />
                <div className="text-sm font-medium">{tab.name}</div>
                <div className="text-xs mt-1 opacity-75">{tab.description}</div>
                
                {!isAvailable && (
                  <div className="absolute top-2 right-2">
                    <span className="text-xs bg-gray-300 text-gray-600 px-2 py-1 rounded-full">
                      {t('manager.modules.testing.comingSoon', {}, 'Kommer snart')}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {renderTabContent()}
      </div>

      {/* Footer Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Bug className="text-blue-600 mt-1 flex-shrink-0" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">{t('manager.modules.testing.footer.title', {}, 'Test og Debugging Informasjon')}</p>
            <p className="mb-2">
              {t('manager.modules.testing.footer.intro', {}, 'Denne seksjonen inneholder verktøy for testing av systemfunksjonalitet og feilsøking. Alle tester kjøres i debug-modus og logger aktivitet for analyse.')}
            </p>
            <div className="text-xs text-blue-600 space-y-1">
              <div>• <strong>RuleGroupEngine:</strong> {t('manager.modules.testing.footer.rulegroup', {}, 'Test systemregler og regelgrupper som styrer lagerprosessene')}</div>
              <div>• <strong>{t('manager.modules.testing.tabs.logging.name', {}, 'Logging System')}:</strong> {t('manager.modules.testing.footer.logging', {}, 'Test frontend logging og feilrapportering')}</div>
              <div>• <strong>Warehouse API:</strong> {t('manager.modules.testing.footer.warehouse', {}, 'Test warehouse endepunkter, autentisering og datahenting')}</div>
              <div>• <strong>SignalR Activity:</strong> {t('manager.modules.testing.footer.signalr', {}, 'Test sanntids lokasjonsaktivitet via WebSocket-tilkobling')}</div>
              <div>• <strong>Queue & Carousel:</strong> {t('manager.modules.testing.footer.queue', {}, 'Test event queue, automatisk reol-bytte og LRU cache')}</div>
              <div>• <strong>Activity Cache:</strong> {t('manager.modules.testing.footer.cache', {}, 'Test event cache, localStorage persistence og midnight reset')}</div>
              <div>• <strong>Heatmap Viz:</strong> {t('manager.modules.testing.footer.heatmap', {}, 'Test heatmap visualisering, intensity calculation og color schemes')}</div>
              <div>• <strong>Statistics Panel:</strong> {t('manager.modules.testing.footer.statistics', {}, 'Test statistikkpanel, aktivitetsfeed og top lokasjoner')}</div>
              <div>• <strong>{t('manager.modules.testing.tabs.settings.name', {}, 'Settings')}:</strong> {t('manager.modules.testing.footer.settings', {}, 'Test dashboard innstillinger og konfigurasjon')}</div>
              <div>• <strong>GS1-128 Parser:</strong> {t('manager.modules.testing.footer.gs1', {}, 'Test og massetest GS1-128 strekkoder med tilde (~) separator')}</div>
              <div>• <strong>Global Scanner:</strong> {t('manager.modules.testing.footer.scanner', {}, 'Test global strekkode-skanning med useGlobalBarcodeScanner hook')}</div>
              <div>• <strong>{t('manager.modules.testing.tabs.print.name', {}, 'Utskrift')}:</strong> {t('manager.modules.testing.footer.print', {}, 'Test etikettutskrift med browser print og WebSocket (silent printing)')}</div>
              <div>• <strong>{t('manager.modules.testing.tabs.putawayDiag.name', {}, 'Put-Away Diagnostikk')}:</strong> {t('manager.modules.testing.footer.putawayDiag', {}, 'Diagnostiser og feilsøk vareplassering med detaljert steg-for-steg analyse')}</div>
              <div>• <strong>{t('manager.modules.testing.tabs.performance.name', {}, 'Ytelse & Performance')}:</strong> {t('manager.modules.testing.footer.performance', {}, 'Kommer snart - overvåkning av systemytelse')}</div>
              <div>• <strong>{t('manager.modules.testing.tabs.validation.name', {}, 'Data Validering')}:</strong> {t('manager.modules.testing.footer.validation', {}, 'Kommer snart - validering av datakonsistens')}</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
